import { useCallback, useEffect, useRef, useState } from 'react';
import { settingsApi } from '../lib/api';
import { useToast } from './use-toast';

const DEFAULTS = {
  zara_avatar: true,
  send_completion_email: true,
  allow_mobile: true,
  enforce_proctoring: false,
  talent_pool_cert: false,
  daily_summary_email: false,
  weekly_summary_email: true,
  on_completion: 'created',
  application_form: {
    phone: 'required', resume: 'required', linkedin: 'required', city: 'required',
  },
};

/**
 * Loads settings once, exposes merged state + a debounced `save` that
 * persists the current state to the backend.
 */
export default function useAppSettings() {
  const [state, setState] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);
  const { toast } = useToast();
  const saveTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    settingsApi.get()
      .then((data) => { if (!cancelled) setState((prev) => ({ ...prev, ...data })); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  const persist = useCallback(async (next) => {
    try {
      await settingsApi.update(next);
    } catch (e) {
      toast({ title: 'Failed to save settings', description: e?.message || 'Network error' });
    }
  }, [toast]);

  const update = useCallback((patch) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => persist(next), 400);
      return next;
    });
  }, [persist]);

  return { state, update, loaded };
}
